import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import AdminClientDetailDrawer from "./AdminClientDetailDrawer";
import AdminFilterToolbar, { AdminFilterField } from "./AdminFilterToolbar";
import PortalSelect from "../PortalSelect";

const MAX_CELL = 16;

/** Renders text truncated to MAX_CELL chars; full text shown on hover via title. */
function TruncCell({ value, className = "" }) {
  const text = (value === null || value === undefined) ? "—" : String(value).trim() || "—";
  const needs = text !== "—" && text.length > MAX_CELL;
  return (
    <span className={className} title={needs ? text : undefined}>
      {needs ? text.slice(0, MAX_CELL) + "…" : text}
    </span>
  );
}

function clientCompany(c) {
  return (
    String(c.companyEmployerName || c.company || "").trim() || "Sin empresa"
  );
}

function clientFullName(c) {
  return [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || "—";
}

/**
 * Panel de clientes: toolbar unificada + popover filtros + chips + tabla.
 */
export default function AdminClientsPanel({
  clients = [],
  total = 0,
  searchQuery,
  onSearchChange,
  loading = false,
}) {
  const [companyFilter, setCompanyFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);

  const companyOptions = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => set.add(clientCompany(c)));
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [clients]);

  const stateOptions = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => {
      const s = String(c.state || "").trim();
      if (s) set.add(s);
    });
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [clients]);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      if (companyFilter && clientCompany(c) !== companyFilter) return false;
      if (stateFilter && String(c.state || "").trim() !== stateFilter) return false;
      return true;
    });
  }, [clients, companyFilter, stateFilter]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, companyFilter, stateFilter, pageSize]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  const hasLocalFilters = Boolean(companyFilter || stateFilter);
  const filterCount = [companyFilter, stateFilter].filter(Boolean).length;
  const rangeFrom = filtered.length === 0 ? 0 : start + 1;
  const rangeTo = Math.min(start + pageSize, filtered.length);

  function clearLocalFilters() {
    setCompanyFilter("");
    setStateFilter("");
  }

  const chips = useMemo(() => {
    const list = [];
    if (companyFilter) {
      list.push({
        id: "company",
        field: "Empresa",
        value: companyFilter,
        onRemove: () => setCompanyFilter(""),
      });
    }
    if (stateFilter) {
      list.push({
        id: "state",
        field: "Estado",
        value: stateFilter,
        onRemove: () => setStateFilter(""),
      });
    }
    return list;
  }, [companyFilter, stateFilter]);

  return (
    <section className="scroll-mt-24 space-y-4">
      <AdminFilterToolbar
        searchValue={searchQuery}
        onSearchChange={onSearchChange}
        searchPlaceholder="Buscar usuarios por nombre, correo, teléfono o empresa…"
        searchAriaLabel="Buscar usuarios"
        filterCount={filterCount}
        onClearAll={hasLocalFilters ? clearLocalFilters : undefined}
        chips={chips}
        actions={
          <Link
            to="/admin/clientes/nuevo"
            className="inline-flex h-11 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-brand-lavender px-5 text-[1rem] font-semibold text-white shadow-md shadow-brand-lavender/25 hover:brightness-105 transition-all"
          >
            <UserPlusIcon className="h-5 w-5" aria-hidden />
            Nuevo usuario
          </Link>
        }
      >
        <AdminFilterField label="Empresa">
          <PortalSelect
            className="w-full"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            aria-label="Filtrar por empresa"
          >
            <option value="">Todas</option>
            {companyOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </PortalSelect>
        </AdminFilterField>
        <AdminFilterField label="Estado">
          <PortalSelect
            className="w-full"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos</option>
            {stateOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </PortalSelect>
        </AdminFilterField>
      </AdminFilterToolbar>

      <div className="overflow-hidden rounded-xl border border-brand-lavender/20 bg-white shadow-sm">
        {loading ? (
          <p className="py-16 text-center portal-meta">Cargando usuarios…</p>
        ) : filtered.length === 0 ? (
          <div className="py-16 px-6 text-center">
            <p className="portal-value font-semibold text-brand-navy mb-1">
              {searchQuery || hasLocalFilters
                ? "Ningún usuario coincide"
                : "Aún no hay usuarios"}
            </p>
            <p className="portal-meta mb-4">
              {searchQuery || hasLocalFilters
                ? "Prueba otro filtro o limpia la búsqueda."
                : "Crea el primero para empezar."}
            </p>
            {!searchQuery && !hasLocalFilters ? (
              <Link
                to="/admin/clientes/nuevo"
                className="inline-flex items-center gap-2 portal-label text-brand-lavender hover:underline"
              >
                Nuevo usuario
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] table-fixed">
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-brand-lavender/15 bg-brand-lilac/20">
                  <th className="portal-table-head px-4 py-3 text-left">Nombre</th>
                  <th className="portal-table-head px-4 py-3 text-left">Correo</th>
                  <th className="portal-table-head px-4 py-3 text-left">Teléfono</th>
                  <th className="portal-table-head px-4 py-3 text-left">Estado</th>
                  <th className="portal-table-head px-4 py-3 text-left">Empresa</th>
                  <th className="portal-table-head px-4 py-3 text-left">Cuenta / subempresa</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((c) => (
                  <tr
                    key={c.userId}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedClient(c)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedClient(c);
                      }
                    }}
                    className="border-b border-brand-lavender/10 last:border-0 hover:bg-brand-lilac/20 transition-colors cursor-pointer"
                  >
                    <td className="portal-table-cell px-4 py-3 align-middle">
                      <span
                        className="block truncate font-semibold text-brand-navy"
                        title={clientFullName(c)}
                      >
                        {clientFullName(c)}
                      </span>
                    </td>
                    <td className="portal-table-cell px-4 py-3 align-middle">
                      {c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          title={c.email}
                          className="block truncate text-brand-lavender hover:underline font-normal"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.email}
                        </a>
                      ) : (
                        <span className="text-brand-gray">—</span>
                      )}
                    </td>
                    <td className="portal-table-cell px-4 py-3 align-middle whitespace-nowrap">
                      {c.phone ? (() => {
                        const ph = String(c.phone);
                        const needs = ph.length > MAX_CELL;
                        return (
                          <a
                            href={`tel:${ph.replace(/\s/g, "")}`}
                            title={needs ? ph : undefined}
                            className="hover:underline font-normal"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {needs ? ph.slice(0, MAX_CELL) + "…" : ph}
                          </a>
                        );
                      })() : "—"}
                    </td>
                    <td className="portal-table-cell px-4 py-3 align-middle">
                      {c.state ? (
                        <span className="inline-flex items-center whitespace-nowrap rounded-full bg-brand-lilac/50 px-2.5 py-1 text-[0.8125rem] font-semibold text-brand-navy">
                          {c.state}
                        </span>
                      ) : (
                        <span className="text-brand-gray">—</span>
                      )}
                    </td>
                    <td className="portal-table-cell px-4 py-3 align-middle">
                      <TruncCell value={clientCompany(c)} />
                    </td>
                    <td className="portal-table-cell px-4 py-3 align-middle">
                      <TruncCell value={c.companyAccountName} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-lavender/15 px-4 py-3 bg-white">
            <div className="flex items-center gap-2 portal-meta">
              <span className="portal-label">Por página</span>
              <PortalSelect
                size="sm"
                className="!w-auto min-w-[4.5rem]"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {[10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </PortalSelect>
            </div>
            <div className="flex items-center gap-3 portal-meta tabular-nums">
              <span>
                Mostrando {rangeFrom}–{rangeTo} de {filtered.length}
                {total && total !== filtered.length ? ` (${total} en total)` : ""}{" "}
                usuario{filtered.length === 1 ? "" : "s"}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={safePage <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-lavender/25 text-brand-navy disabled:opacity-35 hover:bg-brand-lilac/30"
                  aria-label="Página anterior"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-lavender/25 text-brand-navy disabled:opacity-35 hover:bg-brand-lilac/30"
                  aria-label="Página siguiente"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <AdminClientDetailDrawer
        open={Boolean(selectedClient)}
        client={selectedClient}
        onClose={() => setSelectedClient(null)}
      />
    </section>
  );
}
